import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { DataTable } from "@/components/ui/data-table";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Download,
  Calendar,
  User,
  Building,
  FileText,
  Globe,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { downloadResponseFile } from "@/utils/common.utils";
import html2pdf from "html2pdf.js";

// Helper function to format period dates without timezone conversion
const formatPeriodDate = (dateString: string) => {
  if (!dateString) return "N/A";
  try {
    // Extract just the date part (YYYY-MM-DD) from the ISO string
    const datePart = dateString.split("T")[0];
    const [year, month, day] = datePart.split("-");
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return format(date, "MMM dd, yyyy");
  } catch (error) {
    return "N/A";
  }
};

interface InvoiceDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: any | null; // This will now include invoiceFrom, invoiceTo
}

export function InvoiceDetailsModal({
  open,
  onOpenChange,
  invoice,
}: InvoiceDetailsModalProps) {
  const [showAllTransactions, setShowAllTransactions] = useState(false);

  if (!invoice) return null;

  // Prefer full names if provided by parent, fallback to display names
  const invoiceFromName = invoice.fullMerchantName || invoice.merchant;
  const invoiceToName = invoice.fullProgramName || invoice.program;

  const getStatusColor = (status: string) => {
    switch (status) {
      case "paid":
        return "bg-green-100 text-green-800 hover:bg-green-100";
      case "pending":
        return "bg-yellow-100 text-yellow-800 hover:bg-yellow-100";
      case "overdue":
        return "bg-red-100 text-red-800 hover:bg-red-100";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Helper function for currency formatting: show as "amount CODE" (e.g., 200 AED)
  const formatAmountWithCode = (
    value: number,
    currencyCode: string = "USD"
  ) => {
    // Round to 2 decimal places to avoid floating point precision issues
    const rounded = Math.round((Number(value) || 0) * 100) / 100;
    const formatted = rounded.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${formatted} ${currencyCode || "USD"}`.trim();
  };

  // Backward-compatible wrapper used elsewhere in this component
  const formatCurrency = (value: number, currencyCode: string = "USD") =>
    formatAmountWithCode(value, currencyCode);

  // Helper function for number formatting
  const formatNumber = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Helper function for date formatting
  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return format(new Date(dateString), "MMM dd, yyyy");
  };

  const handleDownloadInvoice = async () => {
    const cleanInvoiceId = invoice.id.replace("#", "");
    const API_BASE = import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL;
    try {
      const response = await fetch(
        `${API_BASE}/invoice/v2/generateInvoicePDF?invoiceId=${cleanInvoiceId}`,
        {
          method: "GET",
        }
      );
      if (!response.ok) {
        throw new Error("Failed to download PDF");
      }
      const filename = await downloadResponseFile(
        response,
        `invoice-${cleanInvoiceId}.pdf`
      );
      toast.success(`Invoice ${filename} downloaded successfully!`);
    } catch (error) {
      console.error("Error downloading invoice:", error);
      toast.error(`Failed to download invoice ${invoice.id}`);
    }
  };

  const displayedTransactions = showAllTransactions
    ? invoice.transactions
    : invoice.transactions.slice(0, 5);

  const remainingCount = invoice.transactions.length - 5;

  return (
    <>
      {/* Hidden printable version for PDF export */}
      <div id="invoice-detail-print" style={{ display: "none" }}>
        <div className="max-w-3xl p-6 bg-white text-black">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between p-4 rounded-lg relative overflow-hidden">
            <div className="z-10 w-full flex flex-col md:flex-row md:items-center md:justify-between">
              <div>
                <div className="text-xl font-bold mb-1">
                  Invoice {invoice.id}
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-sm text-gray-500 flex items-center gap-1">
                    Issued: {formatDate(invoice.issueDate)}
                  </span>
                  <span className="text-sm text-gray-500 flex items-center gap-1">
                    Due: {formatDate(invoice.dueDate)}
                  </span>
                </div>
                <div className="mt-1">
                  <span className="text-sm text-gray-500 flex items-center gap-1">
                    Period: {formatDate(invoice.issueDate)} -{" "}
                    {formatDate(invoice.dueDate)}
                  </span>
                </div>
              </div>
              <div className="mt-2 md:mt-0 md:ml-4 flex-shrink-0 flex items-center justify-end">
                <span
                  className={`inline-block px-3 py-1 rounded ${getStatusColor(
                    invoice.status
                  )}`}
                >
                  {invoice.status.charAt(0).toUpperCase() +
                    invoice.status.slice(1)}
                </span>
              </div>
            </div>
          </div>
          <div className="px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-4">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                <h3 className="font-medium text-gray-700 mb-3">Invoice From</h3>
                <p className="font-medium">{invoiceFromName}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                <h3 className="font-medium text-gray-700 mb-3">Invoice To</h3>
                <p className="font-medium">{invoiceToName}</p>
              </div>
            </div>
            <div className="my-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <div className="text-sm text-gray-500">Subtotal</div>
                  <div className="font-medium">
                    {formatAmountWithCode(
                      invoice.subtotal,
                      (invoice as any)?.currencyCode
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Tax Rate</div>
                  <div className="font-medium">{invoice.taxRate}%</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Tax</div>
                  <div className="font-medium">
                    {formatAmountWithCode(
                      invoice.tax,
                      (invoice as any)?.currencyCode
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Total</div>
                  <div className="font-bold text-lg">
                    {formatAmountWithCode(
                      invoice.total,
                      (invoice as any)?.currencyCode
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="my-6">
              <div className="font-semibold text-gray-700 mb-2">
                Transactions
              </div>
              <table className="w-full text-sm border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-2 border-b">Txn Id</th>
                    <th className="p-2 border-b">Date</th>
                    {(invoice as any)?.invoiceType === "program" ? (
                      <th className="p-2 border-b">Program name</th>
                    ) : (invoice as any)?.invoiceType === "merchant" ? (
                      <th className="p-2 border-b">Merchant name</th>
                    ) : null}
                    <th className="p-2 border-b text-center">Points</th>
                    {(invoice as any)?.invoiceType === "program" ? (
                      <th className="p-2 border-b">Amount After Txn Fee</th>
                    ) : (
                      <th className="p-2 border-b text-right">Points Amount</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {invoice.transactions.map((transaction: any) => (
                    <tr key={transaction.id}>
                      <td className="p-2 border-b">{transaction.id}</td>
                      <td className="p-2 border-b">
                        {formatDate(transaction.date)}
                      </td>
                      {(invoice as any)?.invoiceType === "program" ? (
                        <td className="p-2 border-b">
                          {(invoice as any)?.programName || ""}
                        </td>
                      ) : (invoice as any)?.invoiceType === "merchant" ? (
                        <td className="p-2 border-b">
                          {(invoice as any)?.merchantName || ""}
                        </td>
                      ) : null}
                      <td className="p-2 border-b text-center">
                        {formatNumber(transaction.points)}
                      </td>
                      {(invoice as any)?.invoiceType === "program" ? (
                        <td className="p-2 border-b text-right">
                          {formatAmountWithCode(
                            transaction.amountAfterTxnFee,
                            (invoice as any)?.currencyCode
                          )}
                        </td>
                      ) : (
                        <td className="p-2 border-b text-right">
                          {formatAmountWithCode(
                            transaction.amount,
                            (invoice as any)?.currencyCode
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      {/* Actual modal as before */}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between p-4 rounded-lg relative overflow-hidden">
              <div className="z-10 w-full flex flex-col md:flex-row md:items-center md:justify-between">
                <div>
                  <DialogTitle className="text-xl font-bold mb-1">
                    Invoice {invoice.id}
                  </DialogTitle>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm text-gray-500 flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" />
                      Issued: {formatDate(invoice.issueDate)}
                    </span>
                    <span className="text-sm text-gray-500 flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" />
                      Due: {formatDate(invoice.dueDate)}
                    </span>
                  </div>
                  {invoice.startDate && invoice.endDate && (
                    <div className="mt-1">
                      <span className="text-sm text-gray-500 flex items-center gap-1">
                        <FileText className="h-3.5 w-3.5" />
                        Period: {formatPeriodDate(invoice.startDate)} -{" "}
                        {formatPeriodDate(invoice.endDate)}
                      </span>
                    </div>
                  )}
                </div>
                <div className="mt-2 md:mt-0 md:ml-4 flex-shrink-0 flex items-center justify-end">
                  <Badge
                    variant="outline"
                    className={getStatusColor(invoice.status)}
                  >
                    {invoice.status.charAt(0).toUpperCase() +
                      invoice.status.slice(1)}
                  </Badge>
                </div>
              </div>
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-blue-500/20 blur-3xl -z-0"></div>
            </div>
          </DialogHeader>

          <ScrollArea className="max-h-[60vh]">
            <div className="px-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-4">
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                  <h3 className="font-medium text-gray-700 flex items-center gap-2 mb-3">
                    <Globe className="h-4 w-4" /> Invoice From
                  </h3>
                  <p className="font-medium">{invoiceFromName}</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                  <h3 className="font-medium text-gray-700 flex items-center gap-2 mb-3">
                    <Building className="h-4 w-4" /> Invoice To
                  </h3>
                  <p className="font-medium">{invoiceToName}</p>
                </div>
              </div>

              <div className="bg-white shadow-sm rounded-lg border overflow-hidden mb-6">
                <div className="p-4 border-b flex items-center justify-between">
                  <h2 className="font-medium flex items-center gap-2">
                    <FileText className="h-4 w-4" /> Invoice Transactions
                  </h2>
                  <div className="flex items-center gap-2">
                    {!showAllTransactions && remainingCount > 0 && (
                      <span className="text-sm text-gray-500">
                        + {remainingCount} more transactions
                      </span>
                    )}
                  </div>
                </div>

                <DataTable
                  title=""
                  columns={[
                    { header: "Txn Id", accessorKey: "id" },
                    {
                      header: "Date",
                      accessorKey: "date",
                      cell: (row: any) => formatDate(row.date),
                    },
                    ...((invoice as any)?.invoiceType === "program"
                      ? [
                          {
                            header: "Program name",
                            accessorKey: "_programName",
                            cell: () => (invoice as any)?.programName || "",
                          },
                        ]
                      : (invoice as any)?.invoiceType === "merchant"
                      ? [
                          {
                            header: "Merchant name",
                            accessorKey: "_merchantName",
                            cell: () => (invoice as any)?.merchantName || "",
                          },
                        ]
                      : []),
                    {
                      header: "Points",
                      accessorKey: "points",
                      cellClassName: "text-center",
                      headerClassName: "text-center",
                    },
                    ...((invoice as any)?.invoiceType === "program"
                      ? [
                          {
                            header: "Amount After Txn Fee",
                            accessorKey: "amountAfterTxnFee",
                            cell: (row: any) =>
                              formatAmountWithCode(
                                row.amountAfterTxnFee,
                                (invoice as any)?.currencyCode
                              ),
                            cellClassName: "text-right",
                          },
                        ]
                      : [
                          {
                            header: "Points Amount",
                            accessorKey: "amount",
                            cell: (row: any) =>
                              formatAmountWithCode(
                                row.amount,
                                (invoice as any)?.currencyCode
                              ),
                            cellClassName: "text-right",
                            headerClassName: "text-right",
                          },
                        ]),
                  ]}
                  data={displayedTransactions}
                />

                {invoice.transactions.length > 5 && (
                  <div className="p-4 border-t flex justify-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setShowAllTransactions(!showAllTransactions)
                      }
                      className="text-blue-600 hover:text-blue-700"
                    >
                      {showAllTransactions
                        ? "Show Less"
                        : "Show All Transactions"}
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">Subtotal:</span>
                  <span>
                    {formatAmountWithCode(
                      invoice.subtotal,
                      (invoice as any)?.currencyCode
                    )}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">
                    Tax ({invoice.taxRate}%):
                  </span>
                  <span>
                    {formatAmountWithCode(
                      invoice.tax,
                      (invoice as any)?.currencyCode
                    )}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="font-medium">Total:</span>
                  <span className="font-bold text-xl">
                    {formatAmountWithCode(
                      invoice.total,
                      (invoice as any)?.currencyCode
                    )}
                  </span>
                </div>
              </div>
            </div>
          </ScrollArea>

          <DialogFooter className="gap-2 mt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
            <Button
              className="flex items-center gap-2"
              onClick={handleDownloadInvoice}
            >
              <Download className="h-4 w-4" />
              Download Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
